import { CheckOutlined } from '@ant-design/icons';
import { THEME } from '@common/constants/theme.constant';
import { ITabItem } from '@common/general-ui/tabs/tabs.component';
import { ISerializedStylesList } from '@common/interfaces/serialized-styles-list.interface';
import React, { useCallback, useMemo } from 'react';
import { css } from 'twin.macro';
import { ERegisterTabNames } from './register-header.component';
import { useRegister } from './register.hook';

interface IProps {
    onClick?: (tab: ITabItem) => void;
    tab: ITabItem;
    index: number;
    isActive: boolean;
}

export const RegisterNavTab = ({ tab, isActive, index, onClick }: IProps): JSX.Element => {
    const { registerInfo } = useRegister();
    const handleClick = useCallback((): void => onClick && onClick(tab), [onClick, tab]);

    const isValid = useMemo(() => {
        if (tab?.name === ERegisterTabNames.checkDelivery) {
            return registerInfo.isCheckPostCodeStepValid;
        }
        if (tab?.name === ERegisterTabNames.yourFirstBox) {
            return registerInfo.isFirstBoxStepValid;
        }
        if (tab?.name === ERegisterTabNames.yourAccountDetails) {
            return registerInfo.isAccountDetailsStepValid;
        }
        if (tab?.name === ERegisterTabNames.deliveryDetails) {
            return registerInfo.isDeliveryStepValid;
        }
        return false;
    }, [
        registerInfo.isFirstBoxStepValid,
        registerInfo.isDeliveryStepValid,
        tab?.name,
        registerInfo.isCheckPostCodeStepValid,
        registerInfo.isAccountDetailsStepValid,
    ]);

    return (
        <li onClick={handleClick} tw="inline-block list-none text-center" data-testid={`tabitem-${tab.name}`}>
            <div css={styles.stepTab({ isActive, isValid })} tw="flex justify-between gap-1.5 py-1.5 px-2 items-center">
                <div
                    css={styles.stepCounter({ isActive, isValid })}
                    tw="flex w-6 h-6 font-light leading-4 text-xs justify-center items-center"
                >
                    {isValid && !isActive ? <CheckOutlined tw="mt-0" /> : <span tw="mt-[2px]">{index + 1}</span>}
                </div>
                <div tw="mt-[2px] hidden lg:block">{tab.title}</div>
            </div>
        </li>
    );
};

const styles = {
    stepTab: ({ isActive, isValid }: { isActive: boolean; isValid: boolean }): ISerializedStylesList => [
        css`
            font-size: ${THEME.SMALL_FONT_SIZE};
            color: ${THEME.TEXT_BLACK};
            cursor: pointer;

            &:hover {
                background: ${THEME.GRAY_200};
                border-radius: 6px;
            }
        `,
        !isValid &&
            !isActive &&
            css`
                cursor: auto;

                &:hover {
                    background: none;
                }
            `,
    ],
    stepCounter: ({ isActive, isValid }: { isActive: boolean; isValid: boolean }): ISerializedStylesList => [
        css`
            border-radius: 80px;
            border: 1px solid ${THEME.TEXT_BLACK};
        `,
        isValid &&
            css`
                background-color: ${THEME.LIGHT_GREEN};
                border: none;
                color: ${THEME.WHITE};
            `,
        isActive &&
            css`
                background-color: ${THEME.TEXT_BLACK};
                color: ${THEME.WHITE};
            `,
    ],
};
